export const PREVIEW_CHUNK_BYTES = 256 * 1024;
export const MAX_PREVIEW_BYTES = 4 * 1024 * 1024;
export const MAX_PREVIEW_LINES = 20000;
export const MAX_JSON_LINE_LENGTH = 64 * 1024;

export interface PreviewLine {
  number: number;
  text: string;
  complete: boolean;
}

export interface PreviewSnapshot {
  lines: PreviewLineCollection;
  bytesLoaded: number;
  ended: boolean;
  limited: boolean;
  limitReason: 'bytes' | 'lines' | null;
}

export interface PreviewLineCollection extends Iterable<PreviewLine> {
  readonly length: number;
  slice(start: number, end: number): PreviewLine[];
}

// Keep one copy of the complete text and a sparse index. Large files with tiny
// lines do not allocate one retained object per line; raw pages create 100 rows.
class CompleteSessionLines implements PreviewLineCollection {
  readonly length: number;
  private readonly text: string;
  private checkpoints: number[] = [];
  private static readonly STRIDE = 100;

  constructor(text: string) {
    this.text = text;
    let count = 0;
    let offset = 0;
    while (offset < text.length) {
      if (count % CompleteSessionLines.STRIDE === 0)
        this.checkpoints.push(offset);
      count++;
      const newline = text.indexOf('\n', offset);
      offset = newline < 0 ? text.length : newline + 1;
    }
    this.length = count;
  }

  private *rows(start: number, end: number): Generator<PreviewLine> {
    if (start >= end) return;
    const checkpoint = Math.floor(start / CompleteSessionLines.STRIDE);
    let number = checkpoint * CompleteSessionLines.STRIDE;
    let offset = this.checkpoints[checkpoint];
    while (number < end) {
      const newline = this.text.indexOf('\n', offset);
      const stop = newline < 0 ? this.text.length : newline;
      if (number >= start) {
        yield {
          number: number + 1,
          text: this.text.slice(offset, stop),
          complete: true,
        };
      }
      number++;
      offset = stop + 1;
    }
  }

  slice(start: number, end: number): PreviewLine[] {
    return Array.from(
      this.rows(Math.max(0, start), Math.min(this.length, end)),
    );
  }

  [Symbol.iterator](): Generator<PreviewLine> {
    return this.rows(0, this.length);
  }
}

export function decodeCompleteSessionLog(
  content: ArrayBuffer,
  expectedSize: number,
): PreviewSnapshot {
  if (content.byteLength !== expectedSize) {
    throw new Error('日志文件未完整接收，请重试');
  }
  const text = new TextDecoder('utf-8', { ignoreBOM: true }).decode(content);
  return {
    lines: new CompleteSessionLines(text),
    bytesLoaded: content.byteLength,
    ended: true,
    limited: false,
    limitReason: null,
  };
}

// A decoder belongs to one open file. Only append successfully received chunks;
// failed requests must leave its UTF-8 and unfinished-line state untouched.
export class SessionLogPreviewDecoder {
  private decoder = new TextDecoder('utf-8', { ignoreBOM: true });
  private lines: PreviewLine[] = [];
  private pending = '';
  private bytesLoaded = 0;
  private ended = false;
  private lineLimited = false;

  append(contentBase64: string, hasMore: boolean): PreviewSnapshot {
    if (
      this.ended ||
      this.lineLimited ||
      this.bytesLoaded >= MAX_PREVIEW_BYTES
    ) {
      throw new Error('预览已结束或已达到加载上限');
    }
    // Decode and validate before mutating the streaming decoder, so a retry is safe.
    const binary = atob(contentBase64);
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    if (
      bytes.length > PREVIEW_CHUNK_BYTES ||
      this.bytesLoaded + bytes.length > MAX_PREVIEW_BYTES ||
      (hasMore && bytes.length === 0)
    ) {
      throw new Error('预览分段大小异常，请重试');
    }
    const text = this.pending + this.decoder.decode(bytes, { stream: hasMore });
    let offset = 0;
    // Scan only the retained prefix instead of allocating an entry for every
    // newline in the chunk. Tiny or empty lines are bounded as well as bytes.
    while (this.lines.length < MAX_PREVIEW_LINES) {
      const newline = text.indexOf('\n', offset);
      if (newline < 0) break;
      this.lines.push({
        number: this.lines.length + 1,
        text: text.slice(offset, newline),
        complete: true,
      });
      offset = newline + 1;
    }
    this.bytesLoaded += bytes.length;
    this.lineLimited =
      this.lines.length === MAX_PREVIEW_LINES &&
      (offset < text.length || hasMore);
    // Any content after the retained lines is omitted, not a completed JSON row.
    this.pending = this.lineLimited ? '' : text.slice(offset);
    this.ended = !hasMore && !this.lineLimited;
    if (this.ended && this.pending) {
      this.lines.push({
        number: this.lines.length + 1,
        text: this.pending,
        complete: true,
      });
      this.pending = '';
    }
    return this.snapshot();
  }

  snapshot(): PreviewSnapshot {
    const limitReason = this.lineLimited
      ? 'lines'
      : !this.ended && this.bytesLoaded >= MAX_PREVIEW_BYTES
        ? 'bytes'
        : null;
    return {
      lines: this.pending
        ? [
            ...this.lines,
            {
              number: this.lines.length + 1,
              text: this.pending,
              complete: false,
            },
          ]
        : [...this.lines],
      bytesLoaded: this.bytesLoaded,
      ended: this.ended,
      limited: limitReason !== null,
      limitReason,
    };
  }
}

export interface ParsedJsonLine {
  value: unknown;
}

export function parseJsonLine(line: PreviewLine): ParsedJsonLine | undefined {
  if (!line.complete || line.text.length > MAX_JSON_LINE_LENGTH)
    return undefined;
  try {
    const value: unknown = JSON.parse(line.text);
    const pending: { value: unknown; depth: number }[] = [{ value, depth: 0 }];
    let nodes = 0;
    // Bound nesting and node count before formatting: indentation can amplify
    // a deeply nested but short source line into a very large string.
    while (pending.length > 0) {
      const current = pending.pop();
      if (!current) break;
      if (++nodes > 5000 || current.depth > 32) return undefined;
      if (current.value !== null && typeof current.value === 'object') {
        for (const child of Object.values(current.value)) {
          pending.push({ value: child, depth: current.depth + 1 });
        }
      }
    }
    return { value };
  } catch {
    return undefined;
  }
}

export function formatJsonLine(line: PreviewLine): string | undefined {
  const parsed = parseJsonLine(line);
  return parsed ? JSON.stringify(parsed.value, null, 2) : undefined;
}
